/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 *
 * Context nesnesi `theme-context.ts`, hook `useTheme.ts` içinde. Bu dosya yalnızca
 * `ThemeProvider` bileşenini sağlar; eski tüketici sözleşmesi için `useTheme`
 * re-export edilir.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';

import {
  ThemeContext,
  THEME_STORAGE_KEY,
  applyThemeToDocument,
  type Theme,
} from './theme-context';

export { useTheme } from './useTheme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    let initialTheme: Theme = 'light';

    if (saved === 'light' || saved === 'dark') {
      initialTheme = saved;
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      initialTheme = 'dark';
    }

    if (typeof window !== 'undefined') {
      document.documentElement.setAttribute('data-theme', initialTheme);
    }

    return initialTheme;
  });
  const themeRef = useRef(theme);
  const setTheme = useCallback((nextTheme: Theme) => {
    themeRef.current = nextTheme;
    // Canvas consumers resolve CSS tokens while rendering; publish the DOM theme first.
    applyThemeToDocument(nextTheme);
    setThemeState(nextTheme);
  }, []);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Sistem teması değişikliğini dinle (yalnızca kullanıcı manuel seçim yapmamışsa).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const currentSaved = localStorage.getItem(THEME_STORAGE_KEY);
      if (!currentSaved) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }

    return undefined;
  }, [setTheme]);

  // Portal iframe içinde: parent'tan gelen tema değişikliğini dinle.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') !== '1') return undefined;

    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (data?.type === 'QUBEPRO_THEME_CHANGE' && (data.theme === 'light' || data.theme === 'dark')) {
        setTheme(data.theme);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setTheme]);

  const toggleTheme = useCallback(() => {
    setTheme(themeRef.current === 'light' ? 'dark' : 'light');
  }, [setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
